import { useEffect, useState, useCallback } from "react";
import { PlusCircle, Pencil, Trash2, Eye, Globe, GlobeLock } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDateTime, errMsg } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function RootLegal() {
  const [pages, setPages] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/root/legal-pages").then((r) => setPages(r.data.pages)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!pages) return <LoadingRows rows={5} />;

  const openEdit = (p) => { setForm({ title: p.title, content: p.content || "" }); setDialog({ mode: "edit", page: p }); };
  const openAdd = () => { setForm({ title: "", content: "" }); setDialog({ mode: "add" }); };

  const save = async (publish) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      if (dialog.mode === "add") {
        const { data } = await api.post("/root/legal-pages", form);
        if (publish) await api.patch(`/root/legal-pages/${data.id}`, { status: "published" });
      } else {
        await api.patch(`/root/legal-pages/${dialog.page.id}`, publish ? { ...form, status: "published" } : form);
      }
      toast.success(publish ? "Page published" : "Draft saved");
      setDialog(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/root/legal-pages/${p.id}`, { status });
      toast.success(status === "published" ? `"${p.title}" is now public` : `"${p.title}" unpublished`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try { await api.delete(`/root/legal-pages/${p.id}`); toast.success("Page deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="root-legal">
      <PageHeader title="Legal Pages" subtitle="Manage the platform's public legal pages. Published pages appear on the website automatically.">
        <Button data-testid="add-legal-page" onClick={openAdd} className="rounded-full font-bold gradient-bg text-white glow-sm">
          <PlusCircle className="h-4 w-4 mr-2" /> Add New Legal Page
        </Button>
      </PageHeader>

      <TableShell testid="legal-table" head={["Page", "Slug", "Status", "Last Updated", "Actions"]}>
        {pages.map((p) => (
          <TR key={p.id} testid={`legal-row-${p.slug}`}>
            <TD className="font-bold text-sm">{p.title}</TD>
            <TD className="text-xs font-mono text-muted-foreground">/legal/{p.slug}</TD>
            <TD><StatusBadge status={p.status} /></TD>
            <TD className="text-sm text-muted-foreground">{fmtDateTime(p.updated_at)}{p.updated_by ? ` · ${p.updated_by}` : ""}</TD>
            <TD>
              <div className="flex gap-1.5">
                <button data-testid={`legal-edit-${p.slug}`} onClick={() => openEdit(p)} title="Edit"
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`legal-preview-${p.slug}`} onClick={() => setPreview(p)} title="Preview"
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"><Eye className="h-3.5 w-3.5" /></button>
                {p.status === "published" ? (
                  <button data-testid={`legal-unpublish-${p.slug}`} onClick={() => setStatus(p, "draft")} title="Unpublish"
                    className="h-8 w-8 rounded-full border border-amber-500/40 text-amber-500 flex items-center justify-center hover:bg-amber-500/10 transition-colors"><GlobeLock className="h-3.5 w-3.5" /></button>
                ) : (
                  <button data-testid={`legal-publish-${p.slug}`} onClick={() => setStatus(p, "published")} title="Publish"
                    className="h-8 w-8 rounded-full border border-emerald-500/40 text-emerald-500 flex items-center justify-center hover:bg-emerald-500/10 transition-colors"><Globe className="h-3.5 w-3.5" /></button>
                )}
                <button data-testid={`legal-delete-${p.slug}`} onClick={() => remove(p)} title="Delete"
                  className="h-8 w-8 rounded-full border border-rose-500/40 text-rose-500 flex items-center justify-center hover:bg-rose-500/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </TD>
          </TR>
        ))}
      </TableShell>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="legal-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{dialog?.mode === "edit" ? `Edit — ${dialog.page.title}` : "New Legal Page"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="overline-label">Page Title</Label>
              <Input data-testid="legal-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Shipping Policy" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Content</Label>
              <Textarea data-testid="legal-content-input" rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Write the legal page content here…" /></div>
            <div className="flex gap-2 justify-end">
              <Button data-testid="legal-save-draft" variant="outline" onClick={() => save(false)} disabled={busy} className="rounded-full font-bold">Save Draft</Button>
              <Button data-testid="legal-save-publish" onClick={() => save(true)} disabled={busy} className="rounded-full font-bold gradient-bg text-white">
                {busy ? "Saving…" : dialog?.page?.status === "published" ? "Save (Published)" : "Save & Publish"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent data-testid="legal-preview-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{preview?.title}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Public URL: /legal/{preview?.slug} · Status: {preview?.status}</p>
          <div className="text-sm leading-relaxed whitespace-pre-wrap mt-4">{preview?.content || "No content yet."}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
